// Returns awaiting credit in Payables (BL-013 PR 2): parts sent back that the vendor hasn't credited
// yet, oldest first, with the RMA and how long they've been out, tied to the returns awaiting credit
// account (vendorReturns.awaitingCredit). For each, the vendor's credit note entered from the return's
// lines (vendorBills.createCredit with returned lines; it waits in To approve like any credit), or the
// return closed when the vendor won't credit it in full (vendorReturns.close): credited less, left off
// their bill, or kept by them. A refusal says the server's words.

import { useState } from 'react';
import { client } from '../api.ts';
import {
  CLOSE_LABEL,
  closeOutcome,
  creditForm,
  creditLines,
  creditSubtotal,
  daysOutText,
  mayNet,
  type AwaitingRow,
  type CloseChoice,
  type CreditLineForm,
  type VendorReturn,
} from '../inventory/returns-model.ts';
import { MoneySheet } from '../shared/MoneySheet.tsx';
import { useSaveKey } from '../shared/SaveButton.tsx';
import { dayText, money, parseMoney, todayIn } from './model.ts';
import { ClosedMonthHint, Empty, Loading, Money, Panel, errorText, useQuery } from './ui.tsx';
import '../inventory/inventory.css';

interface Awaiting {
  rows: AwaitingRow[];
  openCents: number;
  ledgerCents: number;
  differenceCents: number;
}

export function ReturnsAwaiting({
  toast,
  timeZone,
  version,
  onChanged,
}: {
  toast: (m: string) => void;
  timeZone?: string;
  /** Bumped when a credit is approved elsewhere on the page, so what's open is read again. */
  version: number;
  onChanged: () => void;
}) {
  const q = useQuery<Awaiting>('vendorReturns.awaitingCredit', {}, [version]);
  const [open, setOpen] = useState<{ id: string; doing: 'credit' | 'close' } | null>(null);
  if (!q.data) return null;
  if (q.data.rows.length === 0 && q.data.differenceCents === 0) return null;
  const done = async (said: string) => {
    toast(said);
    setOpen(null);
    await q.reload();
    onChanged();
  };
  return (
    <Panel title="Returns awaiting credit" aside={<Money cents={q.data.openCents} className="amount" />}>
      {q.data.differenceCents !== 0 && (
        <p className="notice warn" role="status">
          The returns awaiting credit account shows {money(q.data.ledgerCents)}; the returns still open come
          to {money(q.data.openCents)}. Something posted there that isn’t a return, or a posting is still on
          its way.
        </p>
      )}
      {q.data.rows.length === 0 ? (
        <Empty>No parts are waiting for a vendor’s credit.</Empty>
      ) : (
        <ul className="rows" aria-label="Returns awaiting credit">
          {q.data.rows.map((r) => (
            <li key={r.returnId} className="inv-row">
              <div className="inv-main">
                <div>
                  <strong>{r.number}</strong> <span>{r.vendorName}</span>
                  {r.daysOut > 30 && <span className="chip say warn">{daysOutText(r.daysOut)}</span>}
                </div>
                <span className="muted small">
                  Sent back {dayText(r.sentOn)}
                  {r.daysOut > 0 && r.daysOut <= 30 ? ` · ${daysOutText(r.daysOut)}` : ''} ·{' '}
                  {r.rma ? `RMA ${r.rma}` : 'no RMA yet'}
                </span>
              </div>
              <Money cents={r.openCents} className="amount" />
              <div className="row-actions">
                <button
                  type="button"
                  className="btn"
                  aria-label={`Enter ${r.vendorName}’s credit for ${r.number}`}
                  onClick={() => setOpen({ id: r.returnId, doing: 'credit' })}
                >
                  Enter their credit
                </button>
                <button
                  type="button"
                  className="btn quiet"
                  aria-label={`Close ${r.number} without a full credit`}
                  onClick={() => setOpen({ id: r.returnId, doing: 'close' })}
                >
                  Close…
                </button>
              </div>
              {open?.id === r.returnId && (
                <ReturnWork
                  id={r.returnId}
                  doing={open.doing}
                  {...(timeZone ? { timeZone } : {})}
                  onClose={() => setOpen(null)}
                  onDone={(said) => void done(said)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </Panel>
  );
}

/** The return read fresh, then its credit form or its close sheet. */
function ReturnWork({
  id,
  doing,
  timeZone,
  onClose,
  onDone,
}: {
  id: string;
  doing: 'credit' | 'close';
  timeZone?: string;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const q = useQuery<VendorReturn>('vendorReturns.get', { id });
  if (!q.data) return <Loading error={q.error} what="the return" retry={() => void q.reload()} />;
  return doing === 'credit' ? (
    <CreditForReturn r={q.data} {...(timeZone ? { timeZone } : {})} onClose={onClose} onDone={onDone} />
  ) : (
    <CloseReturn r={q.data} onClose={onClose} onDone={onDone} />
  );
}

function CreditForReturn({
  r,
  timeZone,
  onClose,
  onDone,
}: {
  r: VendorReturn;
  timeZone?: string;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const [ref, setRef] = useState('');
  const [dated, setDated] = useState(todayIn(timeZone));
  const [lines, setLines] = useState<CreditLineForm[]>(() => creditForm(r));
  const [tax, setTax] = useState('');
  // recoverable tax on a credit is GST or HST, as on a bill; BC PST is in the prices
  const zones = useQuery<{ zones: { components: { code: string }[] }[] }>('tax.listZones', {});
  const taxCode =
    (zones.data?.zones ?? [])
      .flatMap((z) => z.components.map((c) => c.code))
      .find((c) => c === 'HST' || c === 'GST') ?? 'GST';
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const save = useSaveKey(JSON.stringify([r.id, ref, dated, lines, tax]));
  const built = creditLines(lines);
  const taxCents = tax.trim() ? parseMoney(tax) : 0;
  const subtotal = 'lines' in built ? creditSubtotal(built.lines) : 0;
  const problem =
    'problem' in built
      ? built.problem
      : !ref.trim()
        ? 'Their credit note number.'
        : taxCents === null || taxCents < 0
          ? `The ${taxCode} like 1.25, or leave it empty.`
          : null;
  const set = (i: number, patch: Partial<CreditLineForm>) =>
    setLines(lines.map((l, k) => (k === i ? { ...l, ...patch } : l)));
  const enter = async () => {
    if (problem || !('lines' in built) || busy) return;
    setBusy(true);
    setError(null);
    try {
      const c = await client.call<{ number: string }>(
        'vendorBills.createCredit',
        {
          vendorId: r.vendorId,
          vendorRef: ref.trim(),
          billDate: dated,
          lines: built.lines,
          taxes: taxCents ? [{ code: taxCode, taxCents }] : [],
        },
        { idempotencyKey: save.key },
      );
      onDone(`${c.number}, ${r.vendorName}’s credit for ${r.number}, is waiting in To approve.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form
      className="fgrid inline-form"
      aria-label={`${r.vendorName}’s credit for ${r.number}`}
      onSubmit={(e) => {
        e.preventDefault();
        void enter();
      }}
    >
      <label>
        <span className="flabel">Their credit note number</span>
        <input className="field" maxLength={100} value={ref} onChange={(e) => setRef(e.target.value)} />
      </label>
      <label>
        <span className="flabel">Dated</span>
        <input className="field" type="date" value={dated} onChange={(e) => setDated(e.target.value)} />
        <ClosedMonthHint date={dated} />
      </label>
      {/* the lines editor's look is Inventory's (.inventory .po-line) */}
      <div className="inventory full">
        <ul className="po-lines" aria-label="What they credit">
          {lines.map((l, i) => (
            <li key={l.returnLineId} className="po-line">
              <b className="po-line-name">
                {l.name}
                <span className="muted small block">{l.left} left to credit</span>
              </b>
              <label>
                <span className="flabel">How many</span>
                <input
                  className="field"
                  inputMode="decimal"
                  value={l.quantity}
                  onChange={(e) => set(i, { quantity: e.target.value })}
                />
              </label>
              <label>
                <span className="flabel">Each</span>
                <input
                  className="field"
                  inputMode="decimal"
                  value={l.price}
                  onChange={(e) => set(i, { price: e.target.value })}
                />
              </label>
            </li>
          ))}
        </ul>
      </div>
      <label>
        <span className="flabel">{taxCode} on it (optional)</span>
        <input
          className="field"
          inputMode="decimal"
          placeholder="0.00"
          value={tax}
          onChange={(e) => setTax(e.target.value)}
        />
      </label>
      <p className="wide muted small">
        {money(subtotal + (taxCents ?? 0))} in all. It waits in To approve; approved, it clears the return and
        can go on their bills.
      </p>
      {problem && (
        <p className="error full" role="alert">
          {problem}
        </p>
      )}
      {error && (
        <p className="error full" role="alert">
          {error}
        </p>
      )}
      <div className="row-actions full">
        <button type="submit" className="btn primary" disabled={!!problem || busy}>
          {busy ? 'Saving…' : 'Save the credit note'}
        </button>
        <button type="button" className="btn quiet" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CloseReturn({
  r,
  onClose,
  onDone,
}: {
  r: VendorReturn;
  onClose: () => void;
  onDone: (said: string) => void;
}) {
  const choices: CloseChoice[] = mayNet(r) ? ['credited_less', 'netted', 'kept'] : ['credited_less', 'kept'];
  const [how, setHow] = useState<CloseChoice>('credited_less');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const close = async () => {
    setBusy(true);
    setError(null);
    try {
      await client.call('vendorReturns.close', {
        returnId: r.id,
        how,
        ...(note.trim() ? { note: note.trim() } : {}),
      });
      onDone(`${r.number} is closed: ${CLOSE_LABEL[how].toLowerCase()}.`);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <MoneySheet
      title={`Close ${r.number} without a full credit?`}
      outcome={closeOutcome(r, how)}
      confirm="Close the return"
      ready
      busy={busy}
      error={error}
      onConfirm={() => void close()}
      onClose={onClose}
    >
      <fieldset className="choices">
        <legend>What {r.vendorName} did</legend>
        {choices.map((c) => (
          <label key={c} className="checkrow">
            <input type="radio" name="how" checked={how === c} onChange={() => setHow(c)} /> {CLOSE_LABEL[c]}
          </label>
        ))}
      </fieldset>
      <label>
        Note (optional)
        <input
          className="field"
          maxLength={500}
          value={note}
          placeholder="Restocking fee; past their return window"
          onChange={(e) => setNote(e.target.value)}
        />
      </label>
    </MoneySheet>
  );
}
